import React, { useState, useEffect } from "react";
import { UserPlus, Trash2, X, Search, Edit2, Check, AlertCircle, Loader2 } from "lucide-react";
import { logUserUpdate, logUserCreate, logUserDelete } from "../utils/dataLineage";

const StatusBadge = ({ status }) => {
  const getStatusColor = () => {
    const statusLower = (status || '').toLowerCase();
    switch (statusLower) {
      case "active":
        return { bg: 'color-mix(in srgb, var(--cares-green) 14%, var(--cares-surface-card))', color: 'var(--cares-green)', border: 'var(--cares-green)' };
      case "disabled":
        return { bg: 'color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))', color: 'var(--cares-orange)', border: 'var(--cares-orange)' };
      default:
        return { bg: 'var(--cares-surface-board)', color: 'var(--cares-text-label)', border: 'var(--cares-line-strong)' };
    }
  };

  const colors = getStatusColor();

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
      background: colors.bg,
      color: colors.color,
      border: `1px solid ${colors.border}`
    }}>
      {status || 'Unknown'}
    </span>
  );
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    user_id: '',
    email: '',
    role: '',
    reporting_manager: '',
    location: '',
    marital_status: '',
    dob: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch("/api/user_management/user_data");
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      setUsers(data);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError("Failed to load users. Please try again later.");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      if (isEditMode) {
        // Find the original user data to compare changes
        const originalUser = users.find(u => u.employee_id === formData.user_id);

        // Update user - uses query parameters
        const params = new URLSearchParams({
          emp_id: formData.user_id,
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          role: formData.role,
          reporting_manager: formData.reporting_manager,
          location: formData.location,
          marital_status: formData.marital_status,
          dob: formData.dob
        });

        const response = await fetch(`/api/user_management/update-user?${params}`, {
          method: 'POST'
        });

        if (!response.ok) throw new Error('Failed to update user');

        // Log changes to data lineage
        const userName = `${formData.first_name} ${formData.last_name}`;
        if (originalUser) {
          if (originalUser.first_name !== formData.first_name) {
            await logUserUpdate(formData.user_id, userName, 'first_name', originalUser.first_name, formData.first_name);
          }
          if (originalUser.last_name !== formData.last_name) {
            await logUserUpdate(formData.user_id, userName, 'last_name', originalUser.last_name, formData.last_name);
          }
          if (originalUser.email !== formData.email) {
            await logUserUpdate(formData.user_id, userName, 'email', originalUser.email, formData.email);
          }
          if (originalUser.role !== formData.role) {
            await logUserUpdate(formData.user_id, userName, 'role', originalUser.role, formData.role);
          }
          if (originalUser.reporting_manager !== formData.reporting_manager) {
            await logUserUpdate(formData.user_id, userName, 'reporting_manager', originalUser.reporting_manager, formData.reporting_manager);
          }
          if (originalUser.location !== formData.location) {
            await logUserUpdate(formData.user_id, userName, 'location', originalUser.location, formData.location);
          }
          if (originalUser.marital_status !== formData.marital_status) {
            await logUserUpdate(formData.user_id, userName, 'marital_status', originalUser.marital_status, formData.marital_status);
          }
          if (originalUser.date_of_birth !== formData.dob) {
            await logUserUpdate(formData.user_id, userName, 'date_of_birth', originalUser.date_of_birth, formData.dob);
          }
        }
      } else {
        // Add user - uses JSON body
        const response = await fetch('/api/user_management/add-user', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) throw new Error('Failed to add user');

        // Log user creation
        const userName = `${formData.first_name} ${formData.last_name}`;
        await logUserCreate(formData.user_id, userName, formData);
      }

      setSuccessMessage(isEditMode ? 'User updated successfully!' : 'User added successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowAddModal(false);
      setIsEditMode(false);
      resetForm();
      fetchUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(
        `/api/user_management/delete-user?emp_id=${selectedUser['Employee ID']}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) throw new Error('Failed to delete user');

      // Log user deletion
      const userName = `${selectedUser['First Name']} ${selectedUser['Last Name']}`;
      await logUserDelete(selectedUser['Employee ID'], userName);

      setSuccessMessage('User deleted successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowDeleteModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      user_id: '',
      email: '',
      role: '',
      reporting_manager: '',
      location: '',
      marital_status: '',
      dob: ''
    });
  };

  const filteredUsers = users.filter(user =>
    user['Name']?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user['Employee ID']?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user['Role']?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Loader2 style={{ width: '48px', height: '48px', color: 'var(--cares-blue)' }} className="animate-spin" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Page Header - Title removed to avoid duplication with Dashboard Header */}

      {/* Success Message */}
      {successMessage && (
        <div style={{
          background: 'color-mix(in srgb, var(--cares-green) 14%, var(--cares-surface-card))',
          border: '1px solid var(--cares-green)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Check style={{ width: '20px', height: '20px', color: 'var(--cares-green)' }} />
          <span style={{ color: 'var(--cares-green)', fontWeight: '500' }}>{successMessage}</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          background: 'color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))',
          border: '1px solid var(--cares-orange)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <AlertCircle style={{ width: '20px', height: '20px', color: 'var(--cares-orange)' }} />
          <span style={{ color: 'var(--cares-orange)', fontWeight: '500' }}>{error}</span>
        </div>
      )}

      {/* Action Bar */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        {/* Search Bar */}
        <div style={{ position: 'relative', flex: '1', minWidth: '300px', maxWidth: '500px' }}>
          <Search style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '20px',
            height: '20px',
            color: 'var(--cares-text-note)'
          }} />
          <input
            type="text"
            placeholder="Search users by name, ID, or role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 12px 12px 44px',
              border: '1px solid var(--cares-line-card)',
              borderRadius: '12px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = 'var(--cares-blue)'}
            onBlur={(e) => e.target.style.borderColor = 'var(--cares-line-card)'}
          />
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => {
              setIsEditMode(false);
              setShowAddModal(true);
              resetForm();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              background: 'var(--cares-green)',
              color: 'var(--cares-text-max)',
              border: 'none',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 4px 6px rgba(16, 185, 129, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 12px rgba(16, 185, 129, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(16, 185, 129, 0.3)';
            }}
          >
            <UserPlus style={{ width: '18px', height: '18px' }} />
            Add User
          </button>
          <button
            onClick={() => setShowDeleteModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              background: 'var(--cares-orange)',
              color: 'var(--cares-text-max)',
              border: 'none',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 4px 6px rgba(239, 68, 68, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 12px rgba(239, 68, 68, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(239, 68, 68, 0.3)';
            }}
          >
            <Trash2 style={{ width: '18px', height: '18px' }} />
            Remove User
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '16px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--cares-surface-board)', borderBottom: '2px solid var(--cares-line-card)' }}>
                <th style={{
                  padding: '16px 24px',
                  textAlign: 'left',
                  fontSize: '12px',
                  fontWeight: '700',
                  color: 'var(--cares-text-3)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Employee ID
                </th>
                <th style={{
                  padding: '16px 24px',
                  textAlign: 'left',
                  fontSize: '12px',
                  fontWeight: '700',
                  color: 'var(--cares-text-3)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Name
                </th>
                <th style={{
                  padding: '16px 24px',
                  textAlign: 'left',
                  fontSize: '12px',
                  fontWeight: '700',
                  color: 'var(--cares-text-3)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Role
                </th>
                <th style={{
                  padding: '16px 24px',
                  textAlign: 'left',
                  fontSize: '12px',
                  fontWeight: '700',
                  color: 'var(--cares-text-3)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Last Accessed
                </th>
                <th style={{
                  padding: '16px 24px',
                  textAlign: 'left',
                  fontSize: '12px',
                  fontWeight: '700',
                  color: 'var(--cares-text-3)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Status
                </th>
                <th style={{
                  padding: '16px 24px',
                  textAlign: 'center',
                  fontSize: '12px',
                  fontWeight: '700',
                  color: 'var(--cares-text-3)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user, index) => (
                  <tr
                    key={user['Employee ID']}
                    style={{
                      borderBottom: '1px solid var(--cares-line-card)',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--cares-surface-board)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'var(--cares-surface-card)'}
                  >
                    <td style={{ padding: '16px 24px', fontSize: '14px', color: 'var(--cares-text-1)', fontWeight: '500' }}>
                      {user['Employee ID']}
                    </td>
                    <td style={{ padding: '16px 24px', fontSize: '14px', color: 'var(--cares-text-1)' }}>
                      {user['Name']}
                    </td>
                    <td style={{ padding: '16px 24px', fontSize: '14px', color: 'var(--cares-text-label)' }}>
                      {user['Role']}
                    </td>
                    <td style={{ padding: '16px 24px', fontSize: '14px', color: 'var(--cares-text-label)' }}>
                      {user['Last Accessed'] === '' ? 'Never' : user['Last Accessed']}
                    </td>
                    <td style={{ padding: '16px 24px' }}>
                      <StatusBadge status={user['Status']} />
                    </td>
                    <td style={{ padding: '16px 24px', textAlign: 'center' }}>
                      <button
                        onClick={() => {
                          setIsEditMode(true);
                          setSelectedUser(user);
                          setFormData({
                            first_name: user['First Name'] || '',
                            last_name: user['Last Name'] || '',
                            user_id: user['Employee ID'] || '',
                            email: user['Email'] || '',
                            role: user['Role'] || '',
                            reporting_manager: user['Reporting Manager'] || '',
                            location: user['Location'] || '',
                            marital_status: user['Marital Status'] || '',
                            dob: user['Date of Birth'] || ''
                          });
                          setShowAddModal(true);
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 16px',
                          background: 'var(--cares-blue)',
                          color: 'var(--cares-text-max)',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--cares-blue)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'var(--cares-blue)'}
                      >
                        <Edit2 style={{ width: '14px', height: '14px' }} />
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" style={{
                    padding: '48px 24px',
                    textAlign: 'center',
                    fontSize: '14px',
                    color: 'var(--cares-text-note)'
                  }}>
                    {searchTerm ? 'No users found matching your search' : 'No users found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '24px'
        }}>
          <div style={{
            background: 'var(--cares-surface-card)',
            borderRadius: '20px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
              padding: '24px 32px',
              borderRadius: '20px 20px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {isEditMode ? (
                  <Edit2 style={{ width: '24px', height: '24px', color: 'var(--cares-text-max)' }} />
                ) : (
                  <UserPlus style={{ width: '24px', height: '24px', color: 'var(--cares-text-max)' }} />
                )}
                <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: 'var(--cares-text-max)' }}>
                  {isEditMode ? 'Edit User' : 'Add New User'}
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setIsEditMode(false);
                  resetForm();
                }}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
              >
                <X style={{ width: '20px', height: '20px', color: 'var(--cares-text-max)' }} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} style={{ padding: '32px' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '24px'
              }}>
                {/* First Name */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--cares-text-3)',
                    marginBottom: '8px'
                  }}>
                    First Name <span style={{ color: 'var(--cares-orange)' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--cares-line-card)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--cares-blue)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--cares-line-card)'}
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--cares-text-3)',
                    marginBottom: '8px'
                  }}>
                    Last Name <span style={{ color: 'var(--cares-orange)' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--cares-line-card)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--cares-blue)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--cares-line-card)'}
                  />
                </div>

                {/* User ID */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--cares-text-3)',
                    marginBottom: '8px'
                  }}>
                    User ID <span style={{ color: 'var(--cares-orange)' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="user_id"
                    value={formData.user_id}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--cares-line-card)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--cares-blue)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--cares-line-card)'}
                  />
                </div>

                {/* Email */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--cares-text-3)',
                    marginBottom: '8px'
                  }}>
                    Email <span style={{ color: 'var(--cares-orange)' }}>*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--cares-line-card)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--cares-blue)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--cares-line-card)'}
                  />
                </div>

                {/* Role */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--cares-text-3)',
                    marginBottom: '8px'
                  }}>
                    Role <span style={{ color: 'var(--cares-orange)' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--cares-line-card)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--cares-blue)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--cares-line-card)'}
                  />
                </div>

                {/* Reporting Manager */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--cares-text-3)',
                    marginBottom: '8px'
                  }}>
                    Reporting Manager
                  </label>
                  <input
                    type="text"
                    name="reporting_manager"
                    value={formData.reporting_manager}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--cares-line-card)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--cares-blue)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--cares-line-card)'}
                  />
                </div>

                {/* Location */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--cares-text-3)',
                    marginBottom: '8px'
                  }}>
                    Location
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--cares-line-card)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--cares-blue)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--cares-line-card)'}
                  />
                </div>

                {/* Date of Birth */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--cares-text-3)',
                    marginBottom: '8px'
                  }}>
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    name="dob"
                    value={formData.dob}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--cares-line-card)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--cares-blue)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--cares-line-card)'}
                  />
                </div>
              </div>

              {/* Form Actions */}
              <div style={{
                marginTop: '32px',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                paddingTop: '24px',
                borderTop: '1px solid var(--cares-line-card)'
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  style={{
                    padding: '12px 24px',
                    background: 'var(--cares-surface-card)',
                    color: 'var(--cares-text-3)',
                    border: '1px solid var(--cares-line-card)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--cares-surface-board)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'var(--cares-surface-card)'}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    padding: '12px 24px',
                    background: isSubmitting ? 'var(--cares-surface-raised)' : (isEditMode ? 'var(--cares-blue)' : 'var(--cares-green)'),
                    color: 'var(--cares-text-max)',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {isSubmitting && <Loader2 style={{ width: '16px', height: '16px' }} className="animate-spin" />}
                  {isSubmitting ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update User' : 'Add User')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '24px'
        }}>
          <div style={{
            background: 'var(--cares-surface-card)',
            borderRadius: '20px',
            maxWidth: '900px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, var(--cares-orange) 0%, var(--cares-orange) 100%)',
              padding: '24px 32px',
              borderRadius: '20px 20px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Trash2 style={{ width: '24px', height: '24px', color: 'var(--cares-text-max)' }} />
                <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: 'var(--cares-text-max)' }}>
                  Remove User
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedUser(null);
                }}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
              >
                <X style={{ width: '20px', height: '20px', color: 'var(--cares-text-max)' }} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '32px', display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '24px' }}>
              {/* User List */}
              <div>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: '700',
                  color: 'var(--cares-text-3)',
                  marginBottom: '16px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Select User
                </h3>
                <div style={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  border: '1px solid var(--cares-line-card)',
                  borderRadius: '12px',
                  padding: '8px'
                }}>
                  {users.map((user) => (
                    <div
                      key={user['Employee ID']}
                      onClick={() => setSelectedUser(user)}
                      style={{
                        padding: '12px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        marginBottom: '4px',
                        background: selectedUser?.['Employee ID'] === user['Employee ID'] ? 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))' : 'var(--cares-surface-card)',
                        border: selectedUser?.['Employee ID'] === user['Employee ID'] ? '2px solid var(--cares-blue)' : '2px solid transparent',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedUser?.['Employee ID'] !== user['Employee ID']) {
                          e.currentTarget.style.background = 'var(--cares-surface-board)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedUser?.['Employee ID'] !== user['Employee ID']) {
                          e.currentTarget.style.background = 'var(--cares-surface-card)';
                        }
                      }}
                    >
                      <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: 'var(--cares-text-1)' }}>
                        {user['Employee ID']}
                      </p>
                      <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--cares-text-label)' }}>
                        {user['Name']}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* User Details */}
              <div>
                {selectedUser ? (
                  <div>
                    <h3 style={{
                      fontSize: '14px',
                      fontWeight: '700',
                      color: 'var(--cares-text-3)',
                      marginBottom: '16px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Confirm Removal
                    </h3>
                    <div style={{
                      background: 'color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))',
                      border: '1px solid color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))',
                      borderRadius: '12px',
                      padding: '20px',
                      marginBottom: '24px'
                    }}>
                      <p style={{
                        margin: 0,
                        fontSize: '14px',
                        color: 'var(--cares-orange)',
                        fontWeight: '500'
                      }}>
                        ⚠️ Warning: This action cannot be undone. The user will be permanently removed from the system.
                      </p>
                    </div>

                    <div style={{
                      background: 'var(--cares-surface-board)',
                      borderRadius: '12px',
                      padding: '20px',
                      marginBottom: '24px'
                    }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                        <div>
                          <p style={{ margin: 0, fontSize: '12px', color: 'var(--cares-text-label)', fontWeight: '600' }}>
                            Employee ID
                          </p>
                          <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: 'var(--cares-text-1)', fontWeight: '500' }}>
                            {selectedUser['Employee ID']}
                          </p>
                        </div>
                        <div>
                          <p style={{ margin: 0, fontSize: '12px', color: 'var(--cares-text-label)', fontWeight: '600' }}>
                            Name
                          </p>
                          <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: 'var(--cares-text-1)', fontWeight: '500' }}>
                            {selectedUser['Name']}
                          </p>
                        </div>
                        <div>
                          <p style={{ margin: 0, fontSize: '12px', color: 'var(--cares-text-label)', fontWeight: '600' }}>
                            Role
                          </p>
                          <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: 'var(--cares-text-1)', fontWeight: '500' }}>
                            {selectedUser['Role']}
                          </p>
                        </div>
                        <div>
                          <p style={{ margin: 0, fontSize: '12px', color: 'var(--cares-text-label)', fontWeight: '600' }}>
                            Status
                          </p>
                          <p style={{ margin: '4px 0 0 0' }}>
                            <StatusBadge status={selectedUser['Status']} />
                          </p>
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                      <button
                        onClick={() => setSelectedUser(null)}
                        style={{
                          padding: '12px 24px',
                          background: 'var(--cares-surface-card)',
                          color: 'var(--cares-text-3)',
                          border: '1px solid var(--cares-line-card)',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--cares-surface-board)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'var(--cares-surface-card)'}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDelete}
                        disabled={isSubmitting}
                        style={{
                          padding: '12px 24px',
                          background: isSubmitting ? 'var(--cares-surface-raised)' : 'linear-gradient(135deg, var(--cares-orange) 0%, var(--cares-orange) 100%)',
                          color: 'var(--cares-text-max)',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: isSubmitting ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        {isSubmitting && <Loader2 style={{ width: '16px', height: '16px' }} className="animate-spin" />}
                        {isSubmitting ? 'Removing...' : 'Confirm Remove'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    minHeight: '300px',
                    color: 'var(--cares-text-note)'
                  }}>
                    <Trash2 style={{ width: '48px', height: '48px', marginBottom: '16px', opacity: 0.5 }} />
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--cares-text-label)' }}>
                      No user selected
                    </h3>
                    <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
                      Select a user from the list to remove
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;

